import {
  Fragment,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  AlertTriangle,
  Archive,
  ChevronDown,
  Clock,
  ExternalLink,
  GripVertical,
  LayoutGrid,
  Mail,
  Pencil,
  Phone,
  Plus,
  RefreshCcw,
  StickyNote,
  Trash2,
  User,
} from "lucide-react";
import { StartCallButton } from "@/components/calls/StartCallButton";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCorners,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragOverEvent,
  type DragStartEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  FUNDING_STAGES,
  HIRING_SIGNALS_DEFAULT_SUB_SCORES,
} from "@/lib/options";
import { ConfirmExclusionDialog } from "./ConfirmExclusionDialog";
import {
  CountryField,
  FounderOriginField,
  SectorField,
  StateField,
} from "./EditableFields";
import axios from "axios";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { CompanyEmailChip } from "@/components/email/CompanyEmailChip";
import { CompanyEmailCampaigns } from "@/components/email/CompanyEmailCampaigns";
import {
  formatCurrency,
  formatDate,
  formatNumber,
  initials,
} from "@/lib/formatters";
import {
  getEnrichmentColor,
  getStatusColor,
  scoreColor,
} from "@/lib/constants";
import { MA_PROJECT_TYPE_ID } from "@/lib/projectConfig";
import {
  COPIED_FROM_COMPANY_LABEL,
  formatPhone,
  isPhoneNumber,
  phoneCopiedFromCompany,
  phoneForCopy,
} from "@/lib/phone";
import { CopyButton } from "@/components/shared/CopyButton";
import { CfoAssessmentPanel } from "./CfoAssessmentPanel";
import { CfoFounderContacts } from "./CfoFounderContacts";
import { UsGrowthScorePanel } from "@/components/enrichment/cfo/UsGrowthScore";
import { UsGrowthDossier } from "@/components/enrichment/cfo/UsGrowthDossier";
import { isUsGrowthHistory } from "@/lib/usGrowth";
import { CfoActionsPanel } from "./CfoActionsPanel";
import { getCompany, rescoreCompany, updateCompany } from "@/api/companies";
import { getContact } from "@/api/contacts";
import { DroppableColumn } from "./customizer/DroppableColumn";
import { FieldVisibilityCustomizer } from "./customizer/FieldVisibilityCustomizer";
import { LayoutViewSelector } from "./customizer/LayoutViewSelector";
import { ManageLayoutViewsDialog } from "./customizer/ManageLayoutViewsDialog";
import { SaveLayoutViewDialog } from "./customizer/SaveLayoutViewDialog";
import { useConfirm, usePrompt } from "@/components/ui/confirm-dialog";
import { SECTION_FUNDING, SECTION_INFO } from "./customizer/sections";
import { SortableShell } from "./customizer/SortableShell";
import { useEffectiveDetailFields } from "./customizer/useEffectiveDetailFields";
import { useFieldVisibility } from "./customizer/useFieldVisibility";
import { useLayoutViews, readLayoutConfig } from "./customizer/useLayoutViews";
import { useSectionLayout } from "./customizer/useSectionLayout";
import {
  FieldRenderer as DynamicFieldRenderer,
  type FieldValue,
} from "./dynamicFields/FieldRenderer";
import type { DetailField } from "@/api/types";
import type {
  Contact,
  CompanyDetail as CompanyDetailType,
  CompanyDetailLayoutConfig,
  CompanyUpdate,
  EmbeddedContact,
  OutreachStatus,
  PipelineStatus,
  ProjectMember,
  ScoringRule,
} from "@/api/types";
import type { OutreachLoadStatus } from "./OutreachSection";
import { useFeature } from "@/hooks/useFeature";
import { usePermissions } from "@/hooks/usePermissions";
import { ScoreBreakdown } from "./ScoreBreakdown";
import { TagBadge } from "@/components/tags/TagBadge";
import { TagSelector } from "@/components/tags/TagSelector";
import { addTagsToCompany, removeTagFromCompany } from "@/api/tags";
import { ActivityTimeline } from "@/components/activity/ActivityTimeline";
import {
  InlineEditField,
  type InlineEditValue,
} from "./InlineEditField";
import { NotesEditor, type NotesEditorHandle } from "./NotesEditor";
import {
  AttachmentZone,
  type AttachmentZoneHandle,
  type ThumbnailContext,
} from "@/components/attachments/AttachmentZone";
import type { AttachmentRead } from "@/api/types";
import { ScoreOverridePopover } from "./ScoreOverridePopover";
import { ContactDetailSheet } from "@/components/contacts/ContactDetailSheet";
import { ContactEditDialog } from "@/components/contacts/ContactEditDialog";
import { CreateContactDialog } from "@/components/contacts/CreateContactDialog";
import { DeleteContactDialog } from "@/components/contacts/DeleteContactDialog";
import { ArchiveCompanyDialog } from "./ArchiveCompanyDialog";
import { Button } from "@/components/ui/button";
import { CompanyTasksCard } from "@/components/tasks/CompanyTasksCard";
import { FinancialsTableCard } from "./FinancialsTableCard";
import { useCurrentProject } from "@/hooks/useCurrentProject";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { OutreachSection } from "./OutreachSection";
import { HiddenDetail, isHidden } from "@/components/shared/HiddenDetail";

/** Frontend-owned sections — not part of the backend's
 *  detail-fields catalog. Always potentially visible (subject
 *  to feature flags / perms inside the descriptors); appended
 *  to the dynamic default layout when the backend doesn't
 *  declare them. */
const FRONTEND_LEFT_SECTIONS = ["tags", "score", "notes"] as const;
const FRONTEND_RIGHT_SECTIONS = [
  "contacts",
  // The Email module's campaigns, then the legacy panel: never mixed.
  "email_campaigns",
  "outreach",
  "activity",
  "tasks",
] as const;

/** First-paint fallback used while the backend's detail-fields
 *  catalog is still in flight. Lives separately from the
 *  dynamic default below so operators landing on a project
 *  without an active view see a sensible layout immediately. */
const FALLBACK_SECTION_LAYOUT = {
  left: ["info", ...FRONTEND_LEFT_SECTIONS],
  right: [...FRONTEND_RIGHT_SECTIONS],
} as const;

/** Project type id that ships the financials table. Confirmed
 *  against the backend seed (project_types_seed.py step 2 ships
 *  the M&A Sourcing project_type with this exact id). If the id
 *  ever changes we can extend this to a Set. */

/** Country → default tax-id-type mapping. When the operator
 *  changes a company's country and the company has no tax_id_type
 *  yet, we pre-fill it from this table — saves a few clicks for
 *  the common case (US → EIN, France → SIREN, etc.). The "GB"
 *  alias mirrors "UK" so legacy data with either ISO code lights
 *  up. Anything not in the table leaves tax_id_type untouched. */
const COUNTRY_TAX_DEFAULTS: Record<
  string,
  { type: string; label: string }
> = {
  US: { type: "ein", label: "EIN" },
  FR: { type: "siren", label: "SIREN" },
  UK: { type: "company_number", label: "Company Number" },
  GB: { type: "company_number", label: "Company Number" },
  NL: { type: "kvk", label: "KVK" },
  DE: { type: "other", label: "Handelsregister" },
};

/** Default per-rule cap when the scoring rule doesn't carry one. */
const DEFAULT_SIGNAL_MAX_POINTS = 15;

interface SignalEntry {
  key: string;
  points: number;
}

interface DominationInfo {
  dominated: boolean;
  /** Human label of the higher-points signal that's already
   *  active — e.g. "Hiring Finance (15pts)". Set only when
   *  `dominated === true`. */
  by?: string;
}

/**
 * Read the scoring rule for "signaux_recrutement" from the
 * project's effective scoring config and surface its sub_scores
 * as the source of truth for both UI ordering and the
 * dominant-signal logic. Higher-points signals win, so the array
 * is sorted desc by points and the dominant is the first active
 * entry walking from the top.
 *
 * Fallback: when the project doesn't ship a sub_scores map (or
 * doesn't declare the rule at all), the V1 production seed is
 * used so the page still renders the four signals in their usual
 * priority order.
 */
function useSignalPriority(
  scoringRules: Array<ScoringRule | Record<string, unknown>>,
) {
  return useMemo(() => {
    const rule = scoringRules.find(
      (r) => (r as { key?: string }).key === "signaux_recrutement",
    ) as ScoringRule | undefined;

    const subScores =
      (rule?.sub_scores ?? null) ?? HIRING_SIGNALS_DEFAULT_SUB_SCORES;

    const signals: SignalEntry[] = Object.entries(subScores)
      .map(([key, points]) => ({ key, points: Number(points) || 0 }))
      .sort((a, b) => b.points - a.points);

    const maxPoints = rule?.max_points ?? DEFAULT_SIGNAL_MAX_POINTS;

    /** Return the highest-points signal that's currently "active"
     *  on the company's metadata. Booleans are active when true;
     *  the headcount-growth-rate metric is active above 0.5
     *  (matches the backend's scoring threshold). */
    const getDominant = (
      metadata: Record<string, unknown>,
    ): SignalEntry | null => {
      for (const signal of signals) {
        if (signal.key === "headcount_growth_rate") {
          if (Number(metadata[signal.key] ?? 0) > 0.5) return signal;
        } else if (metadata[signal.key] === true) {
          return signal;
        }
      }
      return null;
    };

    /** Whether the given signal is currently dominated by a
     *  higher-points active signal. The UI dims dominated rows
     *  and labels them "Overridden by X (Npts)" so operators
     *  understand why turning that switch on doesn't move the
     *  score (the dominant signal already maxed out the rule). */
    const isDominated = (
      key: string,
      metadata: Record<string, unknown>,
    ): DominationInfo => {
      const dominant = getDominant(metadata);
      if (!dominant || dominant.key === key) return { dominated: false };
      const myPoints = signals.find((s) => s.key === key)?.points ?? 0;
      if (dominant.points <= myPoints) return { dominated: false };
      const label = humanizeSignalKey(dominant.key);
      return { dominated: true, by: `${label} (${dominant.points}pts)` };
    };

    return { signals, maxPoints, getDominant, isDominated, subScores };
  }, [scoringRules]);
}

/** Human label for a signal key — e.g. "is_hiring_cos_coo" →
 *  "Hiring CoS/COO". Falls through a small explicit map first so
 *  the well-known signals get curated labels, then a generic
 *  snake-case → Title Case conversion. */
function humanizeSignalKey(key: string): string {
  const MAP: Record<string, string> = {
    is_hiring_finance: "Hiring Finance",
    is_hiring_cos_coo: "Hiring CoS/COO",
    is_hiring_hr_admin: "Hiring HR/Admin",
    headcount_growth_rate: "Headcount Growth",
  };
  if (MAP[key]) return MAP[key];
  return key
    .replace(/^is_hiring_/, "Hiring ")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

const SCORE_IMPACTING_FIELDS = new Set([
  "sector",
  "headcount",
  "funding_stage",
  // CB-12 renamed the funding amount / date metadata keys. Both
  // spellings stay in the set: the new ones so editing the field from
  // the detail panel still triggers a rescore, the old ones because a
  // company enriched before the migration can still carry them.
  "last_funding_amount_usd",
  "last_funding_date",
  "funding_amount_usd",
  "funding_date",
  "founder_origin",
  "has_finance_lead",
  "is_hiring_finance",
  "is_hiring_cos_coo",
  "is_hiring_hr_admin",
  // Legacy keys kept for backward compat with any in-flight
  // CompanyUpdate payloads that still carry them.
  "has_hr_lead",
  "has_admin_lead",
  // CFO v2 team / hiring / funding signals — editing any of these
  // re-scores the company so the new score shows immediately.
  "finance_team_level",
  "has_cos_coo",
  "hiring_finance_level",
  "hiring_stratops",
  "hiring_hr",
  "has_france_link",
  "is_tech_sector",
  "is_blacklisted_sector",
  "is_excluded_incubator",
]);

/**
 * Slot used to thread the page-level attachments hook + handlers
 * down into the Notes column. The page owns `useAttachments` so its
 * state survives any re-render here, but the rendered card itself
 * needs to live next to Notes (see Phase D in attachments roadmap
 * for the customizer-native migration).
 */
export interface AttachmentsSlot {
  attachments: AttachmentRead[];
  isLoading: boolean;
  cap: number;
  /** Hook's upload returns the created row; the slot keeps that
   *  return type so AttachmentZone's `onUpload` is satisfied
   *  without a runtime adapter. */
  upload: (file: File) => Promise<AttachmentRead>;
  remove: (id: string) => Promise<void>;
  download: (id: string) => Promise<void>;
  canUpload: boolean;
  canRemove: (att: AttachmentRead) => boolean;
  /** Forwarded so the page-wide drag overlay can call
   *  `handleFiles()` on the same component instance — keeps the
   *  validation pipeline in exactly one place. */
  attachmentZoneRef: React.Ref<AttachmentZoneHandle>;
  /** Optional header override — e.g. "Attachments · Company". */
  title?: string;
  /** Image thumbnail entity ref — forwarded to AttachmentZone so
   *  image attachments render real previews via signed URLs.
   *  Optional: omitting it keeps the doc-icon-only Phase A
   *  rendering. */
  imageThumbnails?: ThumbnailContext;
  /** When true, AttachmentZone eagerly prefetches signed URLs
   *  for the first 10 image rows on mount. Otherwise images
   *  lazy-load via the IntersectionObserver. Default off. */
  prefetchImages?: boolean;
}

interface CompanyDetailProps {
  company: CompanyDetailType;
  pipelineStatuses: PipelineStatus[];
  scoringRules: Array<ScoringRule | Record<string, unknown>>;
  members: ProjectMember[];
  projectSlug: string;
  notesRef: React.Ref<NotesEditorHandle>;
  /** Full refetch — used for major actions (status change, manual rescore, log call). */
  onChanged: () => void;
  /** Local merge — used for inline edits that don't trigger a full refetch. */
  onCompanyPatched: (updates: Partial<CompanyDetailType>) => void;
  /** Refresh the activity timeline only — fire after a PATCH that the backend logs. */
  onRefreshActivities: () => Promise<void>;
  /** Fired after the company is archived from this page; lets the parent navigate away. */
  onArchived?: () => void;
  /** Outreach state lifted to the page so its slow Lemlist fetch runs in parallel
   *  with getCompany rather than blocking the section behind it. */
  outreach: OutreachStatus | null;
  outreachStatus: OutreachLoadStatus;
  onOutreachData: (next: OutreachStatus | null) => void;
  onOutreachRefetch: () => Promise<void>;
  /** Action-bar handlers — lifted from CompanyDetailPage so the
   *  bar can share its container with the layout-edit / unsaved
   *  state bars. The page still owns the underlying dialogs and
   *  state. */
  onRescoreClick?: () => void;
  onLogCallClick?: () => void;
  onNoteClick?: () => void;
  /** Page-owned attachments wiring. When provided, the
   *  AttachmentZone Card is rendered in the same column as Notes,
   *  immediately below it. Absent → no attachments UI (used by any
   *  caller that hasn't migrated yet). */
  attachmentsSlot?: AttachmentsSlot;
}

export function CompanyDetail({
  company,
  pipelineStatuses,
  scoringRules,
  members,
  projectSlug,
  notesRef,
  onChanged,
  onCompanyPatched,
  onRefreshActivities,
  onArchived,
  outreach,
  outreachStatus,
  onOutreachData,
  onOutreachRefetch,
  onRescoreClick,
  onLogCallClick,
  onNoteClick,
  attachmentsSlot,
}: CompanyDetailProps) {
  const perms = usePermissions();
  const confirm = useConfirm();
  const promptFn = usePrompt();
  const scoringEnabled = useFeature("scoring");
  const lemlistEnabled = useFeature("lemlist");
  // Project type drives a couple of bespoke renderers — the
  // Financials table for M&A, etc. Reading from the live store
  // rather than threading a prop down so the page picks up a
  // project-type change without a remount.
  const { project: currentProject } = useCurrentProject();
  const isHistoricalUsGrowth = isUsGrowthHistory(company.metadata);
  const [growthRevision, setGrowthRevision] = useState(0);
  // Match against both shapes the backend ships in ProjectRead:
  //   - project_type_id (flat string, required)
  //   - project_type.id (nested via ProjectTypeRead, required)
  // Either one is enough — defensive against a refactor on
  // either side that might trim one of the two paths from a
  // particular response (the public API has both, but a future
  // /me-style endpoint or partial projection might not).
  const isMAProject =
    currentProject?.project_type_id === MA_PROJECT_TYPE_ID ||
    currentProject?.project_type?.id === MA_PROJECT_TYPE_ID;

  // Dev-time check — drop into the console once per project
  // change so it's easy to confirm the gate is firing for the
  // M&A page. Useful when the Financials section "doesn't
  // appear" — first thing to check is whether either id path
  // matches the seeded id.
  useEffect(() => {
    if (!currentProject) return;
     
    console.debug("[CompanyDetail] project_type_id check", {
      project_type_id: currentProject.project_type_id,
      "project_type.id": currentProject.project_type?.id,
      expected: MA_PROJECT_TYPE_ID,
      isMAProject,
    });
  }, [currentProject, isMAProject]);
  // The page is heavily inline-editable; "isEditing disabled" lines below
  // were originally gated on a coarse isViewer prop. We now read each
  // capability from the permissions hook directly.
  const isReadOnly = !perms.canEdit;
  async function patch(fieldKey: string, payload: CompanyUpdate) {
    const isScored = SCORE_IMPACTING_FIELDS.has(fieldKey);
    try {
      const updated = await updateCompany(projectSlug, company.id, payload);
      onCompanyPatched(updated);
      if (isScored && !currentProject?.cfo_effective_scoring) {
        const rescored = await rescoreCompany(projectSlug, company.id);
        // Merge the full rescored Company — effective_score, score, score_details
        // and updated_at all change after a rescore.
        onCompanyPatched(rescored);
      }
      toast.success("Saved");
      void onRefreshActivities();
    } catch (err) {
      toast.error(extractErrorMessage(err));
      throw err;
    }
  }

  function patchTopLevel<K extends keyof CompanyUpdate>(key: K) {
    return async (newValue: InlineEditValue) => {
      await patch(key, { [key]: newValue } as CompanyUpdate);
    };
  }

  function patchMetadata(metaKey: string) {
    return async (newValue: InlineEditValue) => {
      // Only send the delta — backend merges the metadata dict server-side.
      await patch(metaKey, { metadata: { [metaKey]: newValue } });
    };
  }

  async function changeStatus(newStatus: string) {
    if (newStatus === company.pipeline_status) return;
    try {
      await updateCompany(projectSlug, company.id, {
        pipeline_status: newStatus,
      });
      toast.success(
        `Status set to ${labelForStatus(pipelineStatuses, newStatus)}`,
      );
      // Status changes add an activity log entry → full refetch.
      onChanged();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function changeOwner(newOwnerId: string | null) {
    if (newOwnerId === company.owner_id) return;
    try {
      const updated = await updateCompany(projectSlug, company.id, {
        owner_id: newOwnerId,
      });
      onCompanyPatched(updated);
      toast.success(newOwnerId ? "Owner updated" : "Unassigned");
      void onRefreshActivities();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function setOverride(value: number | null) {
    const updated = await updateCompany(projectSlug, company.id, {
      score_override: value,
    });
    onCompanyPatched(updated);
    toast.success(value === null ? "Override removed" : "Override applied");
    void onRefreshActivities();
  }

  // Contact sheet / edit / create / delete
  const [activeContact, setActiveContact] = useState<Contact | null>(null);
  const [contactSheetOpen, setContactSheetOpen] = useState(false);
  const [contactEditOpen, setContactEditOpen] = useState(false);
  const [createContactOpen, setCreateContactOpen] = useState(false);
  const [contactToDelete, setContactToDelete] =
    useState<EmbeddedContact | null>(null);
  const [archiveOpen, setArchiveOpen] = useState(false);

  async function openContact(contactId: number) {
    try {
      const fullContact = await getContact(projectSlug, contactId);
      setActiveContact(fullContact);
      setContactSheetOpen(true);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  function handleContactCreated(created: Contact) {
    const next: EmbeddedContact = {
      id: created.id,
      full_name: created.full_name,
      title: created.title,
      email: created.email,
      phone: created.phone,
      linkedin_url: created.linkedin_url,
      is_decision_maker: created.is_decision_maker,
      enrichment_status: created.enrichment_status,
    };
    onCompanyPatched({ contacts: [...company.contacts, next] });
    void onRefreshActivities();
  }

  function handleContactDeleted(deletedId: number) {
    onCompanyPatched({
      contacts: company.contacts.filter((c) => c.id !== deletedId),
    });
    if (activeContact?.id === deletedId) {
      setActiveContact(null);
      setContactSheetOpen(false);
    }
    void onRefreshActivities();
  }

  async function handleAddTag(tagId: number) {
    try {
      const updated = await addTagsToCompany(projectSlug, company.id, [tagId]);
      onCompanyPatched({ tags: updated.tags });
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  }

  async function handleRemoveTag(tagId: number) {
    const previous = company.tags;
    onCompanyPatched({ tags: company.tags.filter((t) => t.id !== tagId) });
    try {
      const updated = await removeTagFromCompany(
        projectSlug,
        company.id,
        tagId,
      );
      onCompanyPatched({ tags: updated.tags });
    } catch (err) {
      onCompanyPatched({ tags: previous });
      toast.error(extractErrorMessage(err));
    }
  }

  function handleContactSaved(updated: Contact) {
    setActiveContact(updated);
    const newContacts: EmbeddedContact[] = company.contacts.map((c) =>
      c.id === updated.id
        ? {
            id: updated.id,
            full_name: updated.full_name,
            title: updated.title,
            email: updated.email,
            phone: updated.phone,
            linkedin_url: updated.linkedin_url,
            is_decision_maker: updated.is_decision_maker,
            enrichment_status: updated.enrichment_status,
          }
        : c,
    );
    onCompanyPatched({ contacts: newContacts });
  }

  const ownerMember = members.find((m) => m.id === company.owner_id) ?? null;
  const ownerLabel = ownerMember?.full_name ?? "Unassigned";
  const statusColors = getStatusColor(company.pipeline_status, pipelineStatuses);

  // Resolve customisable sections against the backend catalog.
  // Only Info and Funding go through the dynamic visibility +
  // ordering system — they have a mix of locked + non-locked
  // fields, so the customiser is meaningful. Founder, Hiring
  // signals, Tags, Score breakdown and Notes are rendered fixed.
  const sectionFallbacks = useMemo(
    () => ({
      info: SECTION_INFO,
      // CFO v2 drops founder_origin (replaced by has_france_link). Strip
      // it from the funding fallback for non-M&A so the field-merge in
      // useEffectiveDetailFields doesn't re-append it to the customiser
      // when the backend's CFO catalog intentionally omits it. M&A keeps
      // the full fallback.
      funding: isMAProject
        ? SECTION_FUNDING
        : {
            ...SECTION_FUNDING,
            fields: SECTION_FUNDING.fields.filter(
              (f) => f.key !== "founder_origin",
            ),
          },
    }),
    [isMAProject],
  );
  const effectiveDetailFields = useEffectiveDetailFields(projectSlug, {
    fallback: sectionFallbacks,
  });
  const effectiveSections = effectiveDetailFields.sections;
  const backendSectionKeys = effectiveDetailFields.backendSectionKeys;

  /** True only when the backend's detail-fields catalog explicitly
   *  ships this section for the current project type. Sections
   *  with hardcoded fallbacks (info, funding) still resolve via
   *  effectiveSections, but the gate below uses backendSectionKeys
   *  so a project that doesn't declare e.g. "funding" doesn't
   *  surface the card. */
  const backendShipsSection = useCallback(
    (key: string) => backendSectionKeys.has(key),
    [backendSectionKeys],
  );

  // useEffectiveDetailFields merges fallback fields the backend
  // didn't ship, so SECTION_FUNDING.founder_origin and
  // SECTION_INFO.state always make it through to the customiser
  // even when the project_type catalog skips them.
  const infoVis = useFieldVisibility(projectSlug, effectiveSections.info);
  const fundingVis = useFieldVisibility(
    projectSlug,
    effectiveSections.funding,
  );

  // Helper: "show the ⚙ trigger only if this section has at least
  // one non-locked field", per the V1 rule. A section where every
  // field is locked (Founder, Signals) gives the operator nothing
  // to toggle, so the trigger would be confusing.
  const hasCustomisableFields = useCallback(
    (section: { fields: { locked?: boolean }[] }) =>
      section.fields.some((f) => !f.locked),
    [],
  );

  // Hiring-signals ordering + points come from the project's
  // scoring rule for "signaux_recrutement". The card iterates
  // sub_scores so adding a new signal in Settings → Scoring is a
  // no-code change here; the priority of the signals (= the order
  // they're checked when computing the dominant signal) is also
  // sub_scores-driven, since higher points = higher priority.
  const signalPriority = useSignalPriority(scoringRules);
  const dominantSignal = useMemo(
    () => signalPriority.getDominant(company.metadata),
    [signalPriority, company.metadata],
  );

  /** Default two-column layout, computed from the backend's
   *  detail-fields response. Backend-shipped sections honour
   *  their declared `column` and `order`; frontend-owned
   *  sections (tags, notes, contacts, outreach, activity,
   *  tasks, score) are appended to whichever column they
   *  conventionally live in if the backend didn't already
   *  place them. Falls back to FALLBACK_SECTION_LAYOUT before
   *  the fetch resolves so operators don't see an empty page
   *  on first paint. */
  const dynamicDefaultLayout = useMemo(() => {
    const raw = effectiveDetailFields.rawSections;
    const cfoSections = currentProject?.cfo_effective_scoring
      ? ["cfo-professional-contact", "cfo-affiliations"]
      : [];
    if (raw.length === 0) return cfoSections.length
      ? { ...FALLBACK_SECTION_LAYOUT, left: [...FALLBACK_SECTION_LAYOUT.left, ...cfoSections] }
      : FALLBACK_SECTION_LAYOUT;
    const left: string[] = [];
    const right: string[] = [];
    const placed = new Set<string>();
    for (const s of [...raw].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    )) {
      if (placed.has(s.key)) continue;
      placed.add(s.key);
      (s.column === "right" ? right : left).push(s.key);
    }
    for (const k of [...FRONTEND_LEFT_SECTIONS, ...cfoSections]) {
      if (!placed.has(k)) {
        placed.add(k);
        left.push(k);
      }
    }
    for (const k of FRONTEND_RIGHT_SECTIONS) {
      if (!placed.has(k)) {
        placed.add(k);
        right.push(k);
      }
    }
    return { left, right };
  }, [effectiveDetailFields.rawSections, currentProject?.cfo_effective_scoring]);

  // Two-column section layout — toggled into edit mode via the
  // "Customize layout" button at the top of the page. In edit
  // mode every section becomes a draggable card that can move
  // within its column or across to the other one; the saved
  // layout persists per project. Default layout is computed
  // from the backend's detail-fields response so M&A and CFO
  // each get their own column ordering.
  const sectionLayout = useSectionLayout(projectSlug, dynamicDefaultLayout);
  const [layoutEditing, setLayoutEditing] = useState(false);
  const [saveViewOpen, setSaveViewOpen] = useState(false);
  const [manageViewsOpen, setManageViewsOpen] = useState(false);

  // Saved layout views — operator-curated and team-shared
  // section layouts + field visibility. The hook owns the
  // CRUD calls + active-view tracking; applyView below collects
  // the layout payload and pushes it into the relevant per-area
  // hooks.
  const layoutViews = useLayoutViews(projectSlug);
  // Active drag id — drives the DragOverlay and the
  // opacity-30 dim on the source row. Set on dragStart, cleared
  // on dragEnd / dragCancel.
  const [activeDragId, setActiveDragId] = useState<string | null>(null);
  const dndSensors = useSensors(
    // 8px activation distance — matches the multi-container
    // pattern from the dnd-kit docs and avoids accidental drags
    // when the operator is just clicking around.
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  /** Resolve which column an id belongs to. Section keys are
   *  looked up via the layout hook; the literal column ids
   *  ("left" / "right") map to themselves so dropping on a
   *  whole column container is treated as that column. */
  const resolveContainer = useCallback(
    (id: string): "left" | "right" | null => {
      if (id === "left" || id === "right") return id;
      return sectionLayout.columnOf(id);
    },
    [sectionLayout],
  );

  function handleSectionDragStart(event: DragStartEvent) {
    setActiveDragId(String(event.active.id));
  }

  /** Cross-column moves happen here for live feedback — as the
   *  operator drags a card over the other column, it physically
   *  moves there and the SortableContext animates neighbouring
   *  rows. Same-column reordering is left to dragEnd, where
   *  arrayMove gives a single, final placement. */
  function handleSectionDragOver(event: DragOverEvent) {
    const { active, over } = event;
    if (!over) return;
    const activeKey = String(active.id);
    const overKey = String(over.id);
    const activeContainer = resolveContainer(activeKey);
    const overContainer = resolveContainer(overKey);
    if (!activeContainer || !overContainer) return;
    if (activeContainer === overContainer) return;

    // Drop position: on a section row we splice in at that row's
    // index; on the column container itself (empty / past the
    // last item) we append.
    const overSectionKey =
      overKey === "left" || overKey === "right" ? undefined : overKey;
    sectionLayout.moveBetweenColumns(
      activeKey,
      activeContainer,
      overContainer,
      overSectionKey,
    );
  }

  function handleSectionDragEnd(event: DragEndEvent) {
    setActiveDragId(null);
    const { active, over } = event;
    if (!over) return;
    const activeKey = String(active.id);
    const overKey = String(over.id);
    if (activeKey === overKey) return;

    const activeContainer = resolveContainer(activeKey);
    const overContainer = resolveContainer(overKey);
    if (!activeContainer || !overContainer) return;

    // Cross-column moves were already applied during dragOver;
    // dragEnd only needs to handle within-column reordering.
    if (activeContainer === overContainer && overKey !== overContainer) {
      sectionLayout.moveWithinColumn(activeContainer, activeKey, overKey);
    }
  }

  function handleSectionDragCancel() {
    setActiveDragId(null);
  }

  /** Snapshot the current layout — the two-column section_order
   *  + per-section field visibility, in the same shape we save
   *  to the SavedView API. Used both for save-as-view and for
   *  the unsaved-changes detection below. */
  const currentSnapshot = useMemo<CompanyDetailLayoutConfig>(
    () => ({
      section_order: sectionLayout.layout,
      field_visibility: {
        info: infoVis.config,
        funding: fundingVis.config,
      },
    }),
    [sectionLayout.layout, infoVis.config, fundingVis.config],
  );

  // ─── Unsaved-changes detection ────────────────────────────────
  //
  // Two-state machine: a baseline `savedSnapshot` (the layout as
  // last saved / loaded) and a `snapshotReady` flag that gates
  // the comparison. The baseline is captured after a 1s settle
  // period — long enough for backend fetches, layout
  // re-seeding, and per-section hook hydration to complete.
  // Comparison goes through `stableStringify` so JSON key-order
  // differences don't surface as false-positive dirty.
  //
  // The flag resets on company change so navigating between
  // companies doesn't briefly show the banner from a previous
  // page's dirty state.
  const [savedSnapshot, setSavedSnapshot] =
    useState<CompanyDetailLayoutConfig | null>(null);
  const [snapshotReady, setSnapshotReady] = useState(false);

  // Reset baseline on company switch — different page, different
  // dirty state.
  useEffect(() => {
    setSnapshotReady(false);
    setSavedSnapshot(null);
  }, [company.id]);

  // Capture the baseline 1s after the data + layout are stable.
  // Each change to `currentSnapshot` resets the timer, so the
  // snapshot reflects the *final* settled state after backend
  // resolution, view application, and per-section hook
  // hydration. Once captured (snapshotReady === true), the
  // effect short-circuits and changes from this point dirty
  // the page.
  useEffect(() => {
    if (snapshotReady) return;
    // Wait for BOTH fetches to settle. If we baseline before
    // the detail-fields response arrives, project-specific
    // section keys (financials / signals / etc.) won't yet be
    // in sectionByKey and the snapshot would diff against a
    // post-load layout that includes them, lighting up the
    // dirty banner on first paint.
    if (layoutViews.loading) return;
    if (effectiveDetailFields.loading) return;
    const timer = setTimeout(() => {
      setSavedSnapshot(currentSnapshot);
      setSnapshotReady(true);
    }, 1000);
    return () => clearTimeout(timer);
  }, [
    snapshotReady,
    layoutViews.loading,
    effectiveDetailFields.loading,
    currentSnapshot,
  ]);

  const isDirty = useMemo(() => {
    if (!snapshotReady || !savedSnapshot) return false;
    return (
      stableStringify(currentSnapshot) !== stableStringify(savedSnapshot)
    );
  }, [snapshotReady, currentSnapshot, savedSnapshot]);

  /** "Can the operator overwrite this view in place?" — always
   *  yes for their own views; for shared views, only admins
   *  with settings.project.edit. */
  const canUpdateActiveView =
    !!layoutViews.activeView &&
    (layoutViews.activeView.is_mine || perms.canEditProject);

  /** Push a snapshot back into the live hooks. Used by Discard
   *  and the view-applier so reverting and switching views go
   *  through one path. */
  const applySnapshot = useCallback(
    (snapshot: CompanyDetailLayoutConfig) => {
      if (snapshot.section_order)
        sectionLayout.applyLayout(snapshot.section_order);
      if (snapshot.field_visibility?.info)
        infoVis.applyConfig(snapshot.field_visibility.info);
      if (snapshot.field_visibility?.funding)
        fundingVis.applyConfig(snapshot.field_visibility.funding);
    },
    [sectionLayout, infoVis, fundingVis],
  );

  /** Discard the unsaved changes — revert the live hooks to
   *  the last saved snapshot. */
  const handleDiscard = useCallback(() => {
    if (!savedSnapshot) return;
    applySnapshot(savedSnapshot);
  }, [savedSnapshot, applySnapshot]);

  /** Update the currently-active view with the latest layout
   *  config. The backend keys layout_config off
   *  view_type="detail_layout", so the discriminator is sent
   *  alongside; filters carries the same payload for backends
   *  that haven't shipped the structured slot. */
  const handleUpdateActiveView = useCallback(async () => {
    const view = layoutViews.activeView;
    if (!view) return;
    const cfg = currentSnapshot;
    const updated = await layoutViews.update(view.id, {
      view_type: "detail_layout",
      layout_config: cfg,
      filters: cfg as unknown as Record<string, unknown>,
    });
    if (updated) {
      toast.success(`View "${updated.name}" saved`);
      // Mark current state as the new clean baseline so the
      // banner disappears.
      setSavedSnapshot(cfg);
    }
    return updated;
  }, [layoutViews, currentSnapshot]);

  /** "Done moving" — exits drag mode. When the operator has an
   *  updateable active view AND the layout is dirty, save first
   *  so the click reads as a single confirm step rather than
   *  exit-then-save-from-the-banner. Otherwise it just exits
   *  edit mode and the indigo banner takes over. */
  const handleDoneMoving = useCallback(async () => {
    if (
      isDirty &&
      layoutViews.activeView &&
      canUpdateActiveView
    ) {
      await handleUpdateActiveView();
    }
    setLayoutEditing(false);
  }, [
    isDirty,
    layoutViews.activeView,
    canUpdateActiveView,
    handleUpdateActiveView,
  ]);

  /** Push a SavedView's layout_config into the live hooks. We
   *  reset snapshotReady so the debounced capture re-baselines
   *  against the *settled* currentSnapshot once per-section
   *  hooks have finished re-hydrating — capturing the raw input
   *  too eagerly would leave the page falsely marked dirty
   *  because the hooks filter their incoming config through
   *  their known-key set.
   *
   *  Unknown section keys (a CFO view loaded on an M&A project,
   *  or vice-versa) are kept in the layout array — they're
   *  filtered out at render time by sectionByKey lookup, but
   *  preserved in storage so switching back to the right
   *  project type restores the full layout. Stripping them at
   *  apply time used to lose project-specific keys
   *  (financials / signals / etc.) when the apply fired
   *  before the detail-fields fetch settled. */
  const applyView = useCallback(
    (view: typeof layoutViews.activeView) => {
      if (!view) return;
      const cfg = readLayoutConfig(view);
      applySnapshot(cfg);
      layoutViews.setActiveId(view.id);
      setSnapshotReady(false);
    },
    [applySnapshot, layoutViews],
  );

  // First-mount snapshot capture. Three paths:
  //   - Operator already had an active view in localStorage →
  //     re-apply it (so the layout matches the saved view, not
  //     the local edits) and seed the snapshot with the view's
  //     config
  //   - No active view but the project ships a default → apply
  //     the default and snapshot it
  //   - Neither → snapshot whatever the hooks initialised to
  //     (defaults / localStorage state). Edits from this point
  //     dirty the page
  const activeViewId = layoutViews.activeId;
  const appliedFirstRef = useRef(false);
  useEffect(() => {
    if (appliedFirstRef.current) return;
    // Wait for BOTH the views fetch AND the detail-fields
    // fetch to settle. If we apply a view before detail-fields
    // arrive, project-specific section keys (financials etc.)
    // would be unrecognised at render time and the persisted
    // layout could permanently lose them on the next
    // localStorage write.
    if (layoutViews.loading) return;
    if (effectiveDetailFields.loading) return;

    if (activeViewId != null) {
      const view = layoutViews.views.find((v) => v.id === activeViewId);
      if (view) {
        applyView(view);
        appliedFirstRef.current = true;
        return;
      }
      // Stale local id — fall through to default / current.
    }
    if (layoutViews.defaultView) {
      applyView(layoutViews.defaultView);
      appliedFirstRef.current = true;
      return;
    }
    // No view to apply — the debounced snapshot effect will
    // baseline against the settled state automatically once
    // currentSnapshot has been stable for 1s.
    appliedFirstRef.current = true;
  }, [
    activeViewId,
    layoutViews.loading,
    layoutViews.defaultView,
    layoutViews.views,
    effectiveDetailFields.loading,
    applyView,
  ]);

  /** Generic save handler for fields routed through the dynamic
   *  FieldRenderer fallback. Routes the value to either the
   *  first-class column or the metadata bag (per `field.source`)
   *  and reuses the existing `patch` helper so SCORE_IMPACTING_FIELDS
   *  still trigger an automatic rescore. */
  const patchField = useCallback(
    async (field: DetailField, next: FieldValue) => {
      // Dev log to verify the request shape — picks up
      // mis-typed metadata keys, wrong source flags, and any
      // backend-vs-frontend field-name drift.
       
      console.debug("[CompanyDetail] PATCH", {
        key: field.key,
        source: field.source ?? "column",
        metadata_key: field.metadata_key ?? null,
        value: next,
      });
      if (field.source === "metadata") {
        const metaKey = field.metadata_key ?? field.key;
        await patch(metaKey, { metadata: { [metaKey]: next } });
      } else {
        await patch(field.key, { [field.key]: next } as CompanyUpdate);
      }
    },
    // `patch` is defined in the outer scope and closes over
    // company.id / projectSlug / onCompanyPatched — its identity is
    // stable enough to ignore here, in line with the existing
    // patchTopLevel / patchMetadata helpers.
    [],
  );

  /** Read the live value for a field. Metadata fields look up
   *  `company.metadata[metadata_key]`; first-class fields read
   *  from the company root. */
  const readFieldValue = useCallback(
    (field: DetailField): unknown => {
      if (field.source === "metadata") {
        const bag = company.metadata as Record<string, unknown> | undefined;
        if (!bag) return null;
        const key = field.metadata_key ?? field.key;
        return bag[key];
      }
      return (company as unknown as Record<string, unknown>)[field.key];
    },
    [company],
  );

  /** Default fallback renderer — drives any field key that doesn't
   *  have a bespoke renderer below. The dynamic FieldRenderer
   *  type-discriminates on `field.type` (text / number / select /
   *  boolean / currency / date / link / textarea), so a backend
   *  ship-it of `description` or `tax_id_type` lights up
   *  automatically without a frontend change. */
  const renderGeneric = useCallback(
    (field: DetailField) => {
      // Boolean fields render as a Switch with no separate label
      // row — the FieldRenderer's BooleanField doesn't read like
      // a "value next to a label" the way the Information grid
      // does, so for consistency with the legacy ToggleCell, wrap
      // booleans in a row with the field label aligned next to
      // the switch.
      if (
        field.type === "boolean" ||
        field.type === "bool" ||
        field.type === "toggle"
      ) {
        return (
          <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2">
            <span className="text-sm text-slate-700">{field.label}</span>
            <DynamicFieldRenderer
              field={field}
              value={readFieldValue(field)}
              readOnly={isReadOnly}
              onSave={(next) => patchField(field, next)}
              hideLabel
            />
          </div>
        );
      }
      // The company phone outside the reader's call session: locked, not "—", and not
      // editable — a value typed over a hidden one would not be saved.
      if (isHidden(company, field.key)) {
        return (
          <FieldRow label={field.label}>
            <HiddenDetail label="Phone" className="text-sm" />
          </FieldRow>
        );
      }
      return (
        <FieldRow label={field.label}>
          <DynamicFieldRenderer
            field={field}
            value={readFieldValue(field)}
            readOnly={isReadOnly}
            onSave={(next) => patchField(field, next)}
            hideLabel
          />
        </FieldRow>
      );
    },
    [company, isReadOnly, patchField, readFieldValue],
  );

  // Per-field bespoke renderers, keyed by the descriptor's `key`.
  // Anything not in this map falls back to `renderGeneric`. Keep
  // a renderer here only when the field needs custom UX (cascading
  // dropdowns, autocomplete, confirm dialog…) — otherwise the
  // dynamic FieldRenderer covers the type-driven default.
  const informationRenderers = useMemo<Record<string, () => ReactNode>>(
    () => ({
      domain: () => (
        <FieldRow label="Domain">
          <InlineEditField
            variant="text"
            value={company.domain}
            readOnly={isReadOnly}
            placeholder="example.com"
            onSave={patchTopLevel("domain")}
          />
        </FieldRow>
      ),
      sector: () => (
        <FieldRow label="Sector">
          <SectorField
            value={company.sector}
            slug={projectSlug}
            readOnly={isReadOnly}
            onSave={(v) => patchTopLevel("sector")(v)}
          />
        </FieldRow>
      ),
      headcount: () => (
        <FieldRow label="Headcount">
          <InlineEditField
            variant="number"
            value={company.headcount}
            readOnly={isReadOnly}
            display={
              company.headcount != null
                ? formatNumber(company.headcount)
                : null
            }
            onSave={patchTopLevel("headcount")}
          />
        </FieldRow>
      ),
      city: () => (
        <FieldRow label="City">
          <InlineEditField
            variant="text"
            value={company.city}
            readOnly={isReadOnly}
            onSave={patchTopLevel("city")}
          />
        </FieldRow>
      ),
      country: () => (
        <FieldRow label="Country">
          <CountryField
            value={company.country}
            readOnly={isReadOnly}
            onSave={async (v) => {
              // Pre-fill tax_id_type from COUNTRY_TAX_DEFAULTS when
              // the operator changes country and there's no
              // tax_id_type set yet — saves a few clicks for the
              // common case. Bundled into a single PATCH so the
              // operator sees one "Saved" toast.
              const update: CompanyUpdate = { country: v };
              const currentTaxType = (
                company as unknown as Record<string, unknown>
              ).tax_id_type;
              if (
                !currentTaxType &&
                typeof v === "string" &&
                v.length > 0
              ) {
                const def = COUNTRY_TAX_DEFAULTS[v.toUpperCase()];
                if (def) update.tax_id_type = def.type;
              }
              await patch("country", update);
            }}
          />
        </FieldRow>
      ),
      state: () => (
        <FieldRow label="State">
          <StateField
            value={company.state}
            countryValue={company.country}
            readOnly={isReadOnly}
            onSave={(v) => patchTopLevel("state")(v)}
          />
        </FieldRow>
      ),
      source: () => (
        <FieldRow label="Source">
          <span className="text-sm text-slate-500">{company.source}</span>
        </FieldRow>
      ),
      created_at: () => (
        <FieldRow label="Added">
          <span className="text-sm text-slate-500">
            {formatDate(company.created_at)}
          </span>
        </FieldRow>
      ),
      // tax_id is gated on tax_id_type — the value is meaningless
      // without a type to interpret it. When type is empty, show a
      // "Set type first" hint instead of an editable input.
      // Returning null here lets the renderer chain (`?? renderGeneric`)
      // fall through to the generic editor once a type is picked.
      tax_id: () => {
        const hasType = !!(
          company as unknown as Record<string, unknown>
        ).tax_id_type;
        if (hasType) return null;
        return (
          <FieldRow label="Tax ID">
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-not-allowed select-none text-sm text-slate-300">
                    Set type first
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top" className="text-xs">
                  Choose a Tax ID Type before entering a value.
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </FieldRow>
        );
      },
    }),
    [company, isReadOnly, patchTopLevel],
  );

  const fundingRenderers = useMemo<Record<string, () => ReactNode>>(
    () => ({
      // funding_stage + founder_origin use hardcoded, M&A-oriented
      // option lists (FUNDING_STAGES / the 14-origin grouped select), so
      // they're bespoke ONLY for M&A. On CFO (and any non-M&A type) they
      // fall through to the generic renderer, which uses the backend
      // detail-fields options (CFO funding stages incl. love_money /
      // series_d / ipo) — founder_origin is dropped entirely for CFO v2.
      ...(isMAProject
        ? {
            funding_stage: () => (
              <FieldRow label="Funding stage">
                <InlineEditField
                  variant="select"
                  value={asString(company.metadata.funding_stage)}
                  options={FUNDING_STAGES}
                  readOnly={isReadOnly}
                  onSave={patchMetadata("funding_stage")}
                />
              </FieldRow>
            ),
          }
        : {}),
      // Keyed on the catalog's field keys, which CB-12 renamed. These
      // must track that rename: the map is looked up by field key, so a
      // stale key silently falls through to the generic renderer — and
      // the generic `currency` branch formats with formatCurrency's
      // defaults (EUR / fr-FR), which would print a dollar amount with
      // a euro sign rather than fail.
      last_funding_amount_usd: () => (
        <FieldRow label="Funding amount">
          <InlineEditField
            variant="number"
            value={asNumber(company.metadata.last_funding_amount_usd)}
            readOnly={isReadOnly}
            placeholder="USD"
            display={
              typeof company.metadata.last_funding_amount_usd === "number"
                ? formatCurrency(
                    company.metadata.last_funding_amount_usd as number,
                    "USD",
                    "en-US",
                  )
                : null
            }
            onSave={patchMetadata("last_funding_amount_usd")}
          />
        </FieldRow>
      ),
      last_funding_date: () => (
        <FieldRow label="Last funding date">
          <InlineEditField
            variant="date"
            value={asString(company.metadata.last_funding_date)}
            readOnly={isReadOnly}
            display={
              typeof company.metadata.last_funding_date === "string"
                ? formatDate(company.metadata.last_funding_date)
                : null
            }
            onSave={patchMetadata("last_funding_date")}
          />
        </FieldRow>
      ),
      // Founder origin's bespoke 14-origin grouped select + exclusion
      // confirm is an M&A/v1 concept. CFO v2 replaces it with
      // has_france_link, so the renderer is M&A-only; on CFO the field
      // simply isn't in the backend detail-fields catalog.
      ...(isMAProject
        ? {
            founder_origin: () => (
              <FieldRow
                label={
                  <span className="inline-flex items-center gap-1.5">
                    Founder origin
                    <WarningTooltip>
                      &ldquo;US native&rdquo; excludes this company from scoring (score = 0)
                    </WarningTooltip>
                  </span>
                }
              >
                <FounderOriginField
                  value={asString(company.metadata.founder_origin)}
                  readOnly={isReadOnly}
                  onSave={(v) => patchMetadata("founder_origin")(v)}
                />
              </FieldRow>
            ),
          }
        : {}),
    }),
    [company, isReadOnly, patchMetadata, isMAProject],
  );

  // Founder + Signals are rendered fixed (no dynamic field
  // customisation system). See the section descriptors below for
  // the JSX bodies. This block intentionally has no renderer
  // maps — keeping the locked-only sections hardcoded is simpler
  // and matches the V1 spec.

  // Funding stages and founder origins now come from a centralized options
  // module (lib/options.ts) rather than the project's metadata_schema enum.

  return (
    <>
      <div className="space-y-6 pb-24">
      {/* Header */}
      <Card className="px-6 py-5">
        <div className="flex items-start gap-5">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-500 text-xl font-bold text-white shadow-sm">
            {company.name.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-3">
              <h1 className="text-2xl font-semibold tracking-tight text-slate-900">
                {company.name}
              </h1>
              <DropdownMenu>
                <DropdownMenuTrigger asChild disabled={isReadOnly}>
                  <button
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium transition-opacity hover:opacity-80 disabled:cursor-default disabled:hover:opacity-100",
                      statusColors.bg,
                      statusColors.text,
                      statusColors.border,
                    )}
                  >
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.span
                        key={company.pipeline_status}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.15 }}
                        className="inline-flex items-center gap-1.5"
                      >
                        <span
                          className={cn("h-1.5 w-1.5 rounded-full", statusColors.dot)}
                        />
                        <span className="capitalize">
                          {company.pipeline_status.replace(/_/g, " ")}
                        </span>
                      </motion.span>
                    </AnimatePresence>
                    {!isReadOnly && (
                      <ChevronDown className="h-3 w-3 opacity-60" />
                    )}
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuLabel>Set status to</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  {pipelineStatuses
                    .slice()
                    .sort((a, b) => a.order - b.order)
                    .map((s) => (
                      <DropdownMenuItem
                        key={s.key}
                        onSelect={() => changeStatus(s.key)}
                      >
                        {s.label}
                      </DropdownMenuItem>
                    ))}
                </DropdownMenuContent>
              </DropdownMenu>
              <CompanyEmailChip companyId={company.id} />
            </div>
            <div className="mt-1.5 flex items-center gap-3 text-sm">
              {company.domain ? (
                <a
                  href={`https://${company.domain}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-indigo-600 hover:text-indigo-500"
                >
                  {company.domain}
                  <ExternalLink className="h-3 w-3" />
                </a>
              ) : (
                <span className="text-slate-400">No domain</span>
              )}
              {company.sector && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="text-slate-500">{company.sector}</span>
                </>
              )}
              {company.city && (
                <>
                  <span className="text-slate-300">·</span>
                  <span className="text-slate-500">
                    {company.city}
                    {company.country && `, ${company.country}`}
                  </span>
                </>
              )}
            </div>
          </div>
          <Separator orientation="vertical" className="h-12" />
          <div className="text-right">
            <div className="text-[10px] font-medium uppercase tracking-wider text-slate-500">
              Owner
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild disabled={isReadOnly}>
                <button className="mt-1 flex items-center gap-2 rounded-md px-2 py-1 transition-colors hover:bg-slate-50 disabled:cursor-default disabled:hover:bg-transparent">
                  <Avatar className="h-7 w-7 border border-slate-200">
                    <AvatarFallback
                      className={cn(
                        "text-[10px] font-semibold text-white",
                        ownerMember ? "bg-slate-900" : "bg-slate-300",
                      )}
                    >
                      {initials(ownerMember?.full_name ?? "—")}
                    </AvatarFallback>
                  </Avatar>
                  <span
                    className={cn(
                      "text-sm font-medium",
                      ownerMember ? "text-slate-700" : "text-slate-400",
                    )}
                  >
                    {ownerLabel}
                  </span>
                  {!isReadOnly && (
                    <ChevronDown className="h-3 w-3 text-slate-400" />
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="min-w-[200px]">
                <DropdownMenuLabel>Owner</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onSelect={() => changeOwner(null)}>
                  Unassigned
                </DropdownMenuItem>
                {members.length > 0 && <DropdownMenuSeparator />}
                {members.map((m) => (
                  <DropdownMenuItem
                    key={m.id}
                    onSelect={() => changeOwner(m.id)}
                  >
                    {m.full_name}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          {scoringEnabled && !isHistoricalUsGrowth && (
            <>
              <Separator orientation="vertical" className="h-12" />
              {(() => {
                // Project types that don't run through the scoring
                // engine (M&A imports) leave company.score / .effective_score
                // null and put per-rule scores in metadata. Sum them
                // so the score circle reads a meaningful total.
                const fallbackTotal =
                  toFiniteNumber(company.metadata?.finance_score) +
                  toFiniteNumber(company.metadata?.governance_score) +
                  toFiniteNumber(company.metadata?.market_score);
                const computed = company.score ?? fallbackTotal;
                const effective = company.effective_score ?? fallbackTotal;
                return (
                  <ScoreOverridePopover
                    computedScore={computed}
                    override={company.score_override}
                    // Manually overriding the score is a rescore-class
                    // action (it replaces the engine's verdict), so gate
                    // it on companies.rescore — the same permission as the
                    // Rescore action — not the broader companies.edit.
                    readOnly={!perms.canRescore}
                    onApply={(v) => setOverride(v)}
                    onRemove={() => setOverride(null)}
                  >
                    <ScoreCircleButton
                      score={effective}
                      hasOverride={company.score_override !== null}
                      interactive={perms.canRescore}
                    />
                  </ScoreOverridePopover>
                );
              })()}
            </>
          )}
          {perms.canArchive && !company.is_archived && (
            <>
              <Separator orientation="vertical" className="h-12" />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setArchiveOpen(true)}
                className="border-amber-200 text-amber-700 hover:bg-amber-50"
              >
                <Archive className="mr-2 h-3.5 w-3.5" />
                Archive
              </Button>
            </>
          )}
        </div>
      </Card>

      {/* CFO actions — above the customizable sections so a pending
       *  review or missing input is seen whatever the saved layout. */}
      {currentProject?.cfo_effective_scoring &&
        !isHistoricalUsGrowth &&
        perms.canViewAll &&
        perms.canViewScore && (
          <CfoActionsPanel
            key={`${projectSlug}:${company.id}`}
            slug={projectSlug}
            companyId={company.id}
            canAnswer={(perms.canEdit || perms.canWorkEnrichment) && !isReadOnly}
            // A silent merge, not the full reload: the page (and this
            // panel's "Re-scored" confirmation) stays in place.
            onRescored={() => {
              void getCompany(projectSlug, company.id)
                .then(onCompanyPatched)
                .catch(() => undefined);
              void onRefreshActivities();
            }}
          />
        )}

      {(() => {
        // Section descriptors — unified source of truth for both
        // columns. Each `body` is self-contained (already wrapped
        // in a Card or its own component chrome). The layout hook
        // owns column placement + ordering; rendering iterates
        // layout.left / layout.right and pulls bodies from the
        // sectionByKey map.
        type Section = {
          key: string;
          label: string;
          anchorId?: string;
          /** Optional ⚙ trigger placed in the Card header in
           *  normal mode (Information / Funding only). Suppressed
           *  in edit mode — the per-card customiser doesn't
           *  apply while sections are being reorganised. */
          headerActions?: ReactNode;
          /** Already wraps its own card chrome (Outreach,
           *  Tasks). The render path skips the outer Card. */
          selfRendered?: boolean;
          body: ReactNode;
          visible?: boolean;
        };
        const sections: Section[] = [
              {
                key: "info",
                label: "Information",
                anchorId: "company-info",
                headerActions:
                  perms.canCustomizeFields &&
                  hasCustomisableFields(effectiveSections.info) ? (
                    <FieldVisibilityCustomizer
                      section={effectiveSections.info}
                      orderedFields={infoVis.orderedFields}
                      visibleSet={infoVis.visibleSet}
                      onToggle={infoVis.toggle}
                      onReorder={infoVis.reorder}
                      onReset={infoVis.reset}
                    />
                  ) : null,
                body: (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    {infoVis.orderedFields
                      .filter((f) => infoVis.isVisible(f.key))
                      // State is a US-only concept on this page —
                      // hide it for non-US companies even when the
                      // operator's customiser has it toggled on, so
                      // setting country to FR doesn't surface a
                      // meaningless empty State row.
                      .filter(
                        (f) =>
                          f.key !== "state" ||
                          (company.country ?? "").toUpperCase() === "US",
                      )
                      .map((f) => (
                        <Fragment key={f.key}>
                          {informationRenderers[f.key]?.() ?? renderGeneric(f)}
                        </Fragment>
                      ))}
                  </div>
                ),
              },
              {
                // Financials — bespoke 3-year table + Revenue/
                // EBITDA chart. Visible only when the backend's
                // detail-fields catalog declares a "financials"
                // section (M&A project_type does, CFO doesn't),
                // AND the project_type id matches — the bespoke
                // renderer reads M&A-specific metadata keys.
                key: "financials",
                label: "Financials",
                anchorId: "company-financials",
                visible: isMAProject && backendShipsSection("financials"),
                body: (
                  <FinancialsTableCard
                    company={company}
                    onUpdate={(key, value) => {
                      void patchMetadata(key)(value);
                    }}
                  />
                ),
              },
              {
                key: "funding",
                label: "Funding & founder",
                // Hidden on project types that don't declare a
                // "funding" section (M&A's targets aren't
                // raising rounds). Backend = source of truth.
                visible: backendShipsSection("funding") && !isHistoricalUsGrowth,
                headerActions:
                  perms.canCustomizeFields &&
                  hasCustomisableFields(effectiveSections.funding) ? (
                    <FieldVisibilityCustomizer
                      section={effectiveSections.funding}
                      orderedFields={fundingVis.orderedFields}
                      visibleSet={fundingVis.visibleSet}
                      onToggle={fundingVis.toggle}
                      onReorder={fundingVis.reorder}
                      onReset={fundingVis.reset}
                    />
                  ) : null,
                body: (
                  <div className="grid grid-cols-2 gap-x-4 gap-y-3">
                    {fundingVis.orderedFields
                      .filter((f) => fundingVis.isVisible(f.key))
                      .map((f) => (
                        <Fragment key={f.key}>
                          {fundingRenderers[f.key]?.() ?? renderGeneric(f)}
                        </Fragment>
                      ))}
                  </div>
                ),
              },
              {
                // Hiring signals — every field is locked (drives
                // scoring), so no customiser. The toggle list and
                // each signal's points come from the project's
                // scoring rule (sub_scores) so adding a new
                // signal in Settings → Scoring lights up here
                // automatically. Hidden entirely when the scoring
                // feature is off, since the signals only earn
                // their keep as scoring inputs.
                key: "signals",
                // Backend names the section (CFO v2 calls it "Team &
                // Hiring Signals"); fall back to the v1 label.
                label: effectiveSections.signals?.label ?? "Hiring signals",
                // Visible when scoring is on AND the backend's
                // detail-fields catalog declares a "signals" section.
                // M&A doesn't ship signals, so the card hides there.
                visible:
                  scoringEnabled && backendShipsSection("signals"),
                // M&A keeps the v1 signal-domination card (toggles with
                // points). CFO v2 renders the backend-shipped team /
                // hiring fields generically (selects + toggles) — the
                // domination model doesn't apply to the level dropdowns.
                body: isMAProject ? (
                  <div className="grid grid-cols-2 gap-3">
                    <ExclusionToggleCell
                        label="Has Finance Lead"
                        helper="Exclusion filter, if active, score = 0"
                        value={asBool(company.metadata.has_finance_lead)}
                        exclusionValue={true}
                        confirmTitle="Activate exclusion filter?"
                        confirmDescription="Activating this filter will exclude the company from scoring. Its score will drop to 0 and it won't be contacted automatically. Continue?"
                        onSave={patchMetadata("has_finance_lead")}
                        readOnly={isReadOnly}
                      />
                      {signalPriority.signals
                        // Headcount-growth-rate is computed by the
                        // engine from headcount snapshots — no
                        // operator toggle. Skip it from the UI list
                        // even when it's in sub_scores.
                        .filter((s) => s.key !== "headcount_growth_rate")
                        .map((signal) => {
                          const active =
                            company.metadata[signal.key] === true;
                          const isDominant =
                            dominantSignal?.key === signal.key;
                          const { dominated, by } =
                            signalPriority.isDominated(
                              signal.key,
                              company.metadata,
                            );
                          return (
                            <SignalToggle
                              key={signal.key}
                              label={humanizeSignalKey(signal.key)}
                              points={signal.points}
                              pointsActive={isDominant}
                              dominatedBy={dominated ? by : undefined}
                              value={active}
                              editable={!isReadOnly}
                              onChange={(v) =>
                                void patchMetadata(signal.key)(v)
                              }
                            />
                          );
                        })}
                  </div>
                ) : (
                  // `?? []` is defensive only: the `visible` gate above
                  // already requires backendShipsSection("signals"), which
                  // is true only when the backend response carries a
                  // populated signals section (it's not in
                  // sectionFallbacks), so this branch always has ≥1 field.
                  <div className="grid grid-cols-2 gap-3">
                    {(effectiveSections.signals?.fields ?? []).map((f) => (
                      <Fragment key={f.key}>{renderGeneric(f)}</Fragment>
                    ))}
                  </div>
                ),
              },
              {
                key: "tags",
                label: "Tags",
                visible: perms.canViewTags,
                body: (
                  <div className="flex flex-wrap items-center gap-2">
                    {company.tags.length === 0 && !perms.canAssignTags && (
                      <p className="text-sm text-slate-400">No tags.</p>
                    )}
                    {company.tags.map((t) => (
                      <TagBadge
                        key={t.id}
                        tag={t}
                        size="md"
                        onRemove={
                          perms.canAssignTags
                            ? () => void handleRemoveTag(t.id)
                            : undefined
                        }
                      />
                    ))}
                    {perms.canAssignTags && (
                      <TagSelector
                        projectSlug={projectSlug}
                        excludeIds={company.tags.map((t) => t.id)}
                        onPick={handleAddTag}
                      />
                    )}
                  </div>
                ),
              },
              {
                key: "score",
                label: isHistoricalUsGrowth ? "Score CFO" : "Score breakdown",
                anchorId: "company-score",
                visible: scoringEnabled && perms.canViewScore && (!isHistoricalUsGrowth || (perms.canAccessEnrichment && perms.canViewAll)),
                body: isHistoricalUsGrowth ? (
                  <UsGrowthScorePanel slug={projectSlug} companyId={company.id} revision={growthRevision} />
                ) : currentProject?.cfo_effective_scoring ? (
                  <CfoAssessmentPanel slug={projectSlug} companyId={company.id} canRequest={perms.canRescore && perms.canTriggerEngine && !isReadOnly} />
                ) : (
                  <ScoreBreakdown
                    totalScore={company.score}
                    scoreDetails={company.score_details}
                    metadata={company.metadata}
                    scoringRules={scoringRules}
                    scoreOverride={company.score_override}
                    onRemoveOverride={
                      isReadOnly ? undefined : () => setOverride(null)
                    }
                  />
                ),
              },
              {
                key: "notes",
                label: "Notes",
                anchorId: "company-notes",
                body: (
                  <NotesEditor
                    ref={notesRef}
                    projectSlug={projectSlug}
                    companyId={company.id}
                    initialNotes={company.notes}
                    editable={perms.canEditNotes}
                    onSaved={(notes) => {
                      onCompanyPatched({ notes });
                      void onRefreshActivities();
                    }}
                  />
                ),
              },
              {
                // Right-column entries — bodies that already
                // ship their own card chrome are flagged
                // selfRendered so the renderer skips the outer
                // Card wrapper.
                key: "contacts",
                label: isHistoricalUsGrowth ? "Fondateurs" : "Key contacts",
                anchorId: "company-contacts",
                body: (
                  <>
                    {(!isHistoricalUsGrowth || !perms.canAccessEnrichment || !perms.canViewAll || company.contacts.length > 0) && <ContactsList
                      contacts={company.contacts}
                      onPick={openContact}
                      canDelete={perms.canDeleteContacts}
                      onAskDelete={(c) => setContactToDelete(c)}
                      isMA={isMAProject}
                    />}
                    {isHistoricalUsGrowth && perms.canAccessEnrichment && perms.canViewAll && (
                      <UsGrowthDossier
                        key={`growth:${projectSlug}:${company.id}`}
                        variant="company"
                        onChanged={() => setGrowthRevision(v => v + 1)}
                        slug={projectSlug}
                        companyId={company.id}
                      />
                    )}
                    {!isHistoricalUsGrowth && !!currentProject?.cfo_effective_scoring && perms.canViewAll && (
                      <CfoFounderContacts
                        key={`${projectSlug}:${company.id}`}
                        slug={projectSlug}
                        companyId={company.id}
                        contacts={company.contacts}
                      />
                    )}
                    {perms.canCreateContacts && (
                      <div className="mt-3 flex justify-start">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => setCreateContactOpen(true)}
                          className="text-slate-600"
                        >
                          <Plus className="mr-2 h-3.5 w-3.5" />
                          Add a contact
                        </Button>
                      </div>
                    )}
                  </>
                ),
              },
              {
                key: "email_campaigns",
                label: "Email campaigns",
                visible: perms.canViewEmail,
                selfRendered: true,
                body: (
                  <ErrorBoundary
                    fallback={
                      <section className="rounded-plane bg-white p-5 shadow-plane">
                        <p className="text-sm text-slate-400">Email campaigns unavailable</p>
                      </section>
                    }
                  >
                    <CompanyEmailCampaigns
                      // Reloads when the legacy workflow lets the company go
                      // (Remove from campaign): its notice goes with it.
                      key={typeof company.metadata?.lemlist_lead_id === "string" ? "legacy" : "free"}
                      companyId={company.id}
                      projectSlug={projectSlug}
                    />
                  </ErrorBoundary>
                ),
              },
              {
                key: "outreach",
                label: "Outreach",
                visible: lemlistEnabled,
                selfRendered: true,
                body: (
                  <ErrorBoundary
                    fallback={
                      <section className="rounded-plane bg-white p-5 shadow-plane">
                        <p className="text-sm text-slate-400">
                          Outreach data unavailable
                        </p>
                      </section>
                    }
                  >
                    <OutreachSection
                      projectSlug={projectSlug}
                      company={company}
                      outreach={outreach}
                      outreachStatus={outreachStatus}
                      onOutreachData={onOutreachData}
                      onOutreachRefetch={onOutreachRefetch}
                      onActivityRefresh={onRefreshActivities}
                      onCompanyReload={onChanged}
                    />
                  </ErrorBoundary>
                ),
              },
              {
                key: "activity",
                label: "Activity",
                anchorId: "company-activity",
                body: (
                  <ActivityTimeline
                    items={company.activities}
                    members={members}
                    pipelineStatuses={pipelineStatuses}
                    contacts={company.contacts}
                    lemlistLeadUrl={
                      typeof company.metadata?.lemlist_lead_url === "string"
                        ? (company.metadata.lemlist_lead_url as string)
                        : null
                    }
                  />
                ),
              },
              {
                key: "tasks",
                label: "Tasks",
                selfRendered: true,
                body: (
                  <CompanyTasksCard
                    projectSlug={projectSlug}
                    company={{
                      id: company.id,
                      name: company.name,
                      domain: company.domain,
                    }}
                  />
                ),
              },
            ];

            const sectionByKey = new Map(sections.map((s) => [s.key, s]));

            /** Wrap a section body for normal-mode rendering.
             *  Self-rendered sections (Outreach, Tasks) bring
             *  their own card chrome and skip the wrapper;
             *  everything else gets the standard Card with
             *  title + headerActions. In edit mode the
             *  SortableShell provides its own preview frame so
             *  we render the body raw inside it (no Card). */
            const wrapBody = (s: Section): ReactNode => {
              if (s.selfRendered) return s.body;
              return (
                <Card
                  id={s.anchorId}
                  title={s.label}
                  headerActions={s.headerActions}
                >
                  {s.body}
                </Card>
              );
            };

            const renderColumn = (column: "left" | "right") => {
              const keys = sectionLayout.layout[column];
              const visibleSections = keys
                .map((k) => sectionByKey.get(k))
                .filter(
                  (s): s is Section => !!s && s.visible !== false,
                );
              if (layoutEditing) {
                return (
                  <DroppableColumn
                    id={column}
                    isEmpty={visibleSections.length === 0}
                  >
                    <SortableContext
                      items={visibleSections.map((s) => s.key)}
                      strategy={verticalListSortingStrategy}
                    >
                      {visibleSections.map((s) => (
                        <SortableShell
                          key={s.key}
                          sectionKey={s.key}
                          label={s.label}
                          isDragging={activeDragId === s.key}
                        >
                          {s.body}
                        </SortableShell>
                      ))}
                    </SortableContext>
                  </DroppableColumn>
                );
              }
              return (
                <div className="space-y-6">
                  {visibleSections.map((s) => (
                    <Fragment key={s.key}>
                      {wrapBody(s)}
                      {/* Attachments slot — non-customizer section
                       *  pinned directly under Notes wherever the
                       *  user has placed Notes (left or right
                       *  column). Phase D will turn this into a
                       *  real customizer-registered field. */}
                      {s.key === "notes" && attachmentsSlot && (
                        <AttachmentZone
                          ref={attachmentsSlot.attachmentZoneRef}
                          mode="live"
                          title={attachmentsSlot.title ?? "Attachments"}
                          variant="expanded"
                          attachments={attachmentsSlot.attachments}
                          isLoading={attachmentsSlot.isLoading}
                          cap={attachmentsSlot.cap}
                          canUpload={attachmentsSlot.canUpload}
                          canRemove={attachmentsSlot.canRemove}
                          onUpload={attachmentsSlot.upload}
                          onRemove={attachmentsSlot.remove}
                          onDownload={attachmentsSlot.download}
                          imageThumbnails={attachmentsSlot.imageThumbnails}
                          prefetchImages={attachmentsSlot.prefetchImages}
                        />
                      )}
                    </Fragment>
                  ))}
                </div>
              );
            };

            const activeSection = activeDragId
              ? sectionByKey.get(activeDragId)
              : null;

            const toolbar = perms.canCustomizeLayout ? (
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <h3 className="text-[11px] font-medium uppercase tracking-wider text-slate-400">
                    Details
                  </h3>
                  {/* View selector — visible in normal mode only.
                   *  Edit mode hides it because the operator's
                   *  current edits aren't yet a saved view. */}
                  {!layoutEditing && !layoutViews.loading && (
                    <LayoutViewSelector
                      views={layoutViews.views}
                      activeViewId={layoutViews.activeId}
                      defaultViewId={layoutViews.defaultView?.id ?? null}
                      canManageShared={perms.canEditProject}
                      onSelectView={(view) => {
                        if (view) applyView(view);
                      }}
                      onSaveAsNew={() => setSaveViewOpen(true)}
                      onManage={() => setManageViewsOpen(true)}
                      onRenameView={async (view) => {
                        const next = await promptFn({
                          title: "Rename view",
                          description: `Rename "${view.name}" to:`,
                          defaultValue: view.name,
                          confirmLabel: "Rename",
                        });
                        if (!next || next === view.name) return;
                        void layoutViews.update(view.id, { name: next });
                      }}
                      onDeleteView={async (view) => {
                        const ok = await confirm({
                          title: "Delete view",
                          description: `Delete "${view.name}"? This cannot be undone.`,
                          confirmLabel: "Delete",
                          variant: "danger",
                          icon: <Trash2 className="h-5 w-5" />,
                        });
                        if (!ok) return;
                        void layoutViews.remove(view.id);
                      }}
                      onToggleShare={(view) => {
                        void layoutViews.update(view.id, {
                          is_shared: !view.is_shared,
                        });
                      }}
                      onToggleDefault={(view) => {
                        void layoutViews.markDefault(
                          view.id,
                          !view.is_default,
                        );
                      }}
                    />
                  )}
                </div>
                {/* "Move sections" trigger sits next to the
                 *  layout-view selector. Toggling it flips on
                 *  the section-drag mode; the floating bar at
                 *  the bottom changes from action pill to the
                 *  slate "Drag to reorder" pill. */}
                {perms.canCustomizeLayout && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      if (layoutEditing) void handleDoneMoving();
                      else setLayoutEditing(true);
                    }}
                    className={cn(
                      "h-7 px-2 text-xs",
                      layoutEditing
                        ? "bg-indigo-50 text-indigo-600 hover:bg-indigo-100 hover:text-indigo-700"
                        : "text-slate-400 hover:text-indigo-600",
                    )}
                  >
                    <LayoutGrid className="mr-1 h-3.5 w-3.5" />
                    {layoutEditing ? "Done" : "Move sections"}
                  </Button>
                )}
              </div>
            ) : null;

            // Normal mode: 60/40 split (lg:grid-cols-12 with
            // col-span-7 / col-span-5) matching the legacy V1
            // layout. Edit mode: 50/50 (lg:grid-cols-2) so the
            // two drop zones are equal weight and the operator
            // can comfortably drag sections across.
            //
            // Multi-container dnd uses closestCorners (not the
            // closestCenter we use elsewhere) — closestCorners
            // is the dnd-kit recommendation for cross-list moves
            // and gives much steadier drop targets when an item
            // is travelling between columns.
            return (
              <>
                {toolbar}
                {layoutEditing ? (
                  <DndContext
                    sensors={dndSensors}
                    collisionDetection={closestCorners}
                    onDragStart={handleSectionDragStart}
                    onDragOver={handleSectionDragOver}
                    onDragEnd={handleSectionDragEnd}
                    onDragCancel={handleSectionDragCancel}
                  >
                    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                      {renderColumn("left")}
                      {renderColumn("right")}
                    </div>
                    <DragOverlay
                      dropAnimation={{
                        duration: 200,
                        easing: "cubic-bezier(0.18, 0.67, 0.6, 1.22)",
                      }}
                    >
                      {activeSection && (
                        <div className="w-full max-w-md rounded-xl border-2 border-indigo-300 bg-white p-4 opacity-90 shadow-2xl">
                          <div className="flex items-center gap-2">
                            <GripVertical className="h-4 w-4 text-indigo-400" />
                            <span className="text-sm font-medium uppercase tracking-wider text-indigo-600">
                              {activeSection.label}
                            </span>
                          </div>
                        </div>
                      )}
                    </DragOverlay>
                  </DndContext>
                ) : (
                  <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                      {renderColumn("left")}
                    </div>
                    <div className="lg:col-span-5">
                      {renderColumn("right")}
                    </div>
                  </div>
                )}
              </>
            );
          })()}
      </div>

      {/* Single floating bar with three mutually-exclusive
       *  states. AnimatePresence mode="wait" cross-fades between
       *  them: the outgoing variant springs out before the
       *  incoming one springs in, so there's never a moment
       *  where two bars overlap.
       *
       *    layoutEditing → slate "Drag to reorder"
       *    isDirty       → indigo "Layout modified"
       *    otherwise     → white action bar
       *      (Rescore / Note / Start call / Log a call / Move)
       *
       *  All three sit centred at bottom-6, pointer-events-none
       *  on the wrapper so the page beneath stays clickable
       *  except where the bar itself catches input. */}
      <div className="pointer-events-none fixed inset-x-0 bottom-6 z-30 flex justify-end px-8">
        <AnimatePresence mode="wait" initial={false}>
          {layoutEditing ? (
            <motion.div
              key="bar-editing"
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 300, damping: 24 }}
              className="pointer-events-auto flex items-center gap-3 rounded-full bg-slate-900 px-5 py-2.5 text-sm text-white shadow-lg shadow-slate-400/30"
              role="status"
            >
              <LayoutGrid className="h-4 w-4 text-slate-400" />
              <span className="font-medium">Drag to reorder</span>
              <span aria-hidden className="h-4 w-px bg-white/20" />
              <button
                type="button"
                onClick={() => {
                  sectionLayout.reset();
                  setLayoutEditing(false);
                }}
                className="rounded-full bg-white/10 px-2.5 py-1 text-xs transition-colors hover:bg-white/20"
              >
                Reset
              </button>
              <button
                type="button"
                onClick={() => void handleDoneMoving()}
                className="rounded-full bg-white px-3 py-1 text-xs font-medium text-slate-900 transition-colors hover:bg-slate-100"
              >
                {layoutViews.activeView && canUpdateActiveView
                  ? "Save & done"
                  : "Done"}
              </button>
            </motion.div>
          ) : isDirty ? (
            <motion.div
              key="bar-unsaved"
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 300, damping: 24 }}
              className="pointer-events-auto flex items-center gap-3 rounded-full bg-indigo-600 px-5 py-2.5 text-sm text-white shadow-lg shadow-indigo-300/40"
              role="status"
            >
              <span
                aria-hidden
                className="h-2 w-2 animate-pulse rounded-full bg-white/80"
              />
              <span className="font-medium">Layout modified</span>
              <span aria-hidden className="h-4 w-px bg-white/30" />
              <button
                type="button"
                onClick={handleDiscard}
                className="rounded-full bg-white/15 px-2.5 py-1 text-xs transition-colors hover:bg-white/25"
              >
                Discard
              </button>
              {layoutViews.activeView && canUpdateActiveView && (
                <button
                  type="button"
                  onClick={() => void handleUpdateActiveView()}
                  className="rounded-full bg-white/15 px-2.5 py-1 text-xs transition-colors hover:bg-white/25"
                >
                  Update &ldquo;{layoutViews.activeView.name}&rdquo;
                </button>
              )}
              <button
                type="button"
                onClick={() => setSaveViewOpen(true)}
                className="rounded-full bg-white px-3 py-1 text-xs font-medium text-indigo-600 transition-colors hover:bg-indigo-50"
              >
                Save as new
              </button>
            </motion.div>
          ) : (
            <motion.div
              key="bar-actions"
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              transition={{ type: "spring", stiffness: 300, damping: 24 }}
              className="pointer-events-auto flex items-center gap-1 rounded-plane bg-white/95 p-1.5 shadow-elevated backdrop-blur-lg"
            >
              {onRescoreClick && perms.canRescore && !currentProject?.cfo_effective_scoring && (
                <button
                  type="button"
                  onClick={onRescoreClick}
                  className="flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-sm text-indigo-600 transition-colors hover:bg-indigo-50"
                >
                  <RefreshCcw className="h-3.5 w-3.5" />
                  Rescore
                </button>
              )}
              {onNoteClick && perms.canEditNotes && (
                <button
                  type="button"
                  onClick={onNoteClick}
                  className="flex items-center gap-1.5 rounded-xl px-3 py-1.5 text-sm text-slate-700 transition-colors hover:bg-slate-50"
                >
                  <StickyNote className="h-3.5 w-3.5" />
                  Note
                </button>
              )}
              {(onRescoreClick || perms.canEditNotes) &&
                (perms.canStartCallTimer || perms.canLogCall) && (
                  <span aria-hidden className="mx-1 h-6 w-px bg-slate-200" />
                )}
              {perms.canStartCallTimer && projectSlug && (
                <StartCallButton
                  projectSlug={projectSlug}
                  companyId={company.id}
                  companyName={company.name}
                  contacts={company.contacts}
                />
              )}
              {onLogCallClick && perms.canLogCall && (
                <button
                  type="button"
                  onClick={onLogCallClick}
                  className="flex items-center gap-1.5 rounded-xl border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700 transition-colors hover:bg-slate-50"
                >
                  <Phone className="h-3.5 w-3.5" />
                  Log a call
                </button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <ContactDetailSheet
        contact={activeContact}
        company={company}
        projectSlug={projectSlug}
        open={contactSheetOpen}
        onOpenChange={setContactSheetOpen}
        onEdit={() => {
          setContactSheetOpen(false);
          setContactEditOpen(true);
        }}
        onDelete={
          perms.canDeleteContacts && activeContact
            ? () => {
                const target = company.contacts.find(
                  (c) => c.id === activeContact.id,
                );
                if (!target) return;
                setContactSheetOpen(false);
                setContactToDelete(target);
              }
            : undefined
        }
        readOnly={!perms.canEditContacts}
        inCompanyContext
      />

      <ContactEditDialog
        contact={activeContact}
        company={company}
        projectSlug={projectSlug}
        open={contactEditOpen}
        onOpenChange={(o) => {
          setContactEditOpen(o);
          if (!o && activeContact) setContactSheetOpen(true);
        }}
        onSaved={handleContactSaved}
      />

      <CreateContactDialog
        projectSlug={projectSlug}
        open={createContactOpen}
        onOpenChange={setCreateContactOpen}
        company={company}
        onCreated={handleContactCreated}
      />

      <DeleteContactDialog
        projectSlug={projectSlug}
        contact={contactToDelete}
        company={company}
        open={!!contactToDelete}
        onOpenChange={(o) => {
          if (!o) setContactToDelete(null);
        }}
        onDeleted={handleContactDeleted}
      />

      <ArchiveCompanyDialog
        mode="single"
        projectSlug={projectSlug}
        open={archiveOpen}
        onOpenChange={setArchiveOpen}
        company={company}
        contactCount={company.contacts.length}
        onArchived={() => {
          setArchiveOpen(false);
          onArchived?.();
        }}
      />

      <SaveLayoutViewDialog
        open={saveViewOpen}
        onOpenChange={setSaveViewOpen}
        canShare={perms.canEditProject}
        onSubmit={async ({ name, is_shared }) => {
          // Snapshot the live layout + visibility config and
          // ship it through the views API. Backend stores the
          // payload on `filters` (compat) and the optional
          // `layout_config` slot.
          const cfg = currentSnapshot;
          const created = await layoutViews.create({
            name,
            entity: "company_detail_layout",
            // The backend rejects layout_config on a request that
            // doesn't declare view_type="detail_layout" — that's
            // the discriminator that says "this view ships a
            // structured layout payload, not the table-style
            // columns/sort/filters shape".
            view_type: "detail_layout",
            filters: cfg as unknown as Record<string, unknown>,
            layout_config: cfg,
            is_shared,
          });
          if (created) {
            layoutViews.setActiveId(created.id);
            // Mark current state as the new clean baseline so
            // the dirty-banner disappears on save.
            setSavedSnapshot(cfg);
          }
          return created;
        }}
      />

      <ManageLayoutViewsDialog
        open={manageViewsOpen}
        onOpenChange={setManageViewsOpen}
        views={layoutViews.views}
        canManageShared={perms.canEditProject}
        onRename={(id, name) => layoutViews.update(id, { name })}
        onDelete={(id) => layoutViews.remove(id)}
        onToggleShare={(id, shared) =>
          layoutViews.update(id, { is_shared: shared })
        }
        onSetDefault={(id, on) => layoutViews.markDefault(id, on)}
      />
    </>
  );
}

function FieldRow({
  label,
  children,
}: {
  label: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="space-y-0.5">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="text-sm font-medium text-slate-900">{children}</div>
    </div>
  );
}

function WarningTooltip({ children }: { children: ReactNode }) {
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            tabIndex={0}
            className="inline-flex cursor-help items-center rounded-full focus:outline-none focus:ring-2 focus:ring-amber-300/40"
          >
            <AlertTriangle className="h-3 w-3 text-amber-500" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs">
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

function ExclusionToggleCell({
  label,
  helper,
  value,
  exclusionValue,
  confirmTitle,
  confirmDescription,
  onSave,
  readOnly,
}: {
  label: string;
  helper: string;
  value: boolean | null;
  exclusionValue: boolean;
  confirmTitle: string;
  confirmDescription: string;
  onSave: (newValue: InlineEditValue) => Promise<void>;
  readOnly?: boolean;
}) {
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);

  async function tryToggle(newValue: boolean) {
    if (newValue === exclusionValue && value !== exclusionValue) {
      setConfirming(true);
      return;
    }
    await commit(newValue);
  }

  async function commit(newValue: boolean) {
    setBusy(true);
    try {
      await onSave(newValue);
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50/30 px-3 py-2.5">
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <WarningTooltip>{helper}</WarningTooltip>
          <span className="truncate text-sm text-slate-700">{label}</span>
        </div>
        <Switch
          checked={!!value}
          disabled={readOnly || busy}
          onCheckedChange={tryToggle}
        />
      </div>
      <ConfirmExclusionDialog
        open={confirming}
        onOpenChange={setConfirming}
        title={confirmTitle}
        description={confirmDescription}
        confirmLabel="Activate filter"
        busy={busy}
        onConfirm={() => commit(exclusionValue)}
      />
    </>
  );
}

/**
 * Bordered Switch row used by the Hiring signals card. Carries
 * a points badge (active = indigo, otherwise grey) and an
 * optional "Overridden by …" subtitle when the signal is
 * dominated by a higher-priority active signal — that's the
 * moment to dim the row so the operator understands why flipping
 * the switch wouldn't move the score.
 */
function SignalToggle({
  label,
  points,
  pointsActive,
  dominatedBy,
  value,
  editable,
  onChange,
}: {
  label: string;
  points?: number;
  pointsActive?: boolean;
  /** When set, the row is dimmed and a small "Overridden by …"
   *  subtitle explains which signal is currently winning. */
  dominatedBy?: string;
  value: boolean | null;
  editable: boolean;
  onChange?: (next: boolean) => void;
}) {
  return (
    <div
      className={cn(
        "flex items-center justify-between rounded-lg border border-slate-200 bg-white px-3 py-2",
        dominatedBy && "opacity-50",
      )}
    >
      <div className="flex min-w-0 flex-col">
        <div className="flex items-center gap-1.5">
          <span className="truncate text-sm text-slate-700">{label}</span>
          {points != null && (
            <span
              className={cn(
                "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] tabular-nums",
                pointsActive
                  ? "bg-indigo-100 font-medium text-indigo-700"
                  : "bg-slate-100 text-slate-400",
              )}
            >
              {points}pts
            </span>
          )}
        </div>
        {dominatedBy && (
          <span className="mt-0.5 text-[10px] text-amber-500">
            Overridden by {dominatedBy}
          </span>
        )}
      </div>
      <Switch
        checked={!!value}
        disabled={!editable}
        onCheckedChange={editable ? onChange : undefined}
      />
    </div>
  );
}

function ContactsList({
  contacts,
  onPick,
  canDelete,
  onAskDelete,
  isMA,
}: {
  contacts: EmbeddedContact[];
  onPick: (contactId: number) => void;
  canDelete: boolean;
  onAskDelete: (contact: EmbeddedContact) => void;
  /** When true, surfaces M&A-specific scoring inputs (age,
   *  tenure_years) read off the contact's `metadata` (older
   *  responses shipped `extra_metadata`; both are accepted). */
  isMA: boolean;
}) {
  if (contacts.length === 0) {
    return <p className="text-sm text-slate-400">No contacts yet.</p>;
  }
  return (
    <ul className="space-y-2">
      {contacts.map((c) => {
        const enrichmentColors = getEnrichmentColor(c.enrichment_status);
        return (
          <li key={c.id} className="group/contact relative">
            {/* A clickable row rather than a <button>: it holds the copy
             *  buttons of its email and number. */}
            <div
              role="button"
              tabIndex={0}
              onClick={() => onPick(c.id)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  onPick(c.id);
                }
              }}
              className="group flex w-full cursor-pointer items-start gap-3 rounded-lg px-3 py-3 text-left transition-colors duration-150 hover:bg-indigo-500/[0.04] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/40"
            >
              <Avatar className="h-9 w-9 border border-slate-200">
                <AvatarFallback className="bg-indigo-100 text-xs font-semibold text-indigo-700">
                  {initials(c.full_name)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex flex-wrap items-baseline gap-2">
                  <span className="font-medium text-slate-900">
                    {c.full_name}
                  </span>
                  {c.is_decision_maker && (
                    <span className="rounded-full bg-indigo-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-indigo-700">
                      DM
                    </span>
                  )}
                  <span
                    className={cn(
                      "rounded-full border px-1.5 py-0.5 text-[10px] font-medium",
                      enrichmentColors.bg,
                      enrichmentColors.text,
                      enrichmentColors.border,
                    )}
                  >
                    {c.enrichment_status}
                  </span>
                </div>
                {c.title && (
                  <div className="text-xs text-slate-500">{c.title}</div>
                )}
                {isMA && (
                  <ContactScoringSignals
                    meta={c.metadata ?? c.extra_metadata}
                  />
                )}
                <div className="flex flex-wrap items-center gap-3 pt-1 text-xs">
                  {c.email && (
                    <span className="inline-flex items-center gap-1 text-indigo-600">
                      <Mail className="h-3 w-3" />
                      {c.email}
                      <CopyButton value={c.email} label="email" />
                    </span>
                  )}
                  {isHidden(c, "email") && <HiddenDetail label="Email" />}
                  {isPhoneNumber(c.phone) && (
                    <span className="inline-flex items-center gap-1 text-slate-500">
                      <Phone className="h-3 w-3" />
                      {formatPhone(c.phone)}
                      <CopyButton value={phoneForCopy(c.phone)} label="phone number" />
                      {phoneCopiedFromCompany(c) && (
                        <span className="text-[11px] italic text-slate-400">
                          ({COPIED_FROM_COMPANY_LABEL})
                        </span>
                      )}
                    </span>
                  )}
                  {isHidden(c, "phone") && <HiddenDetail label="Phone" />}
                  {c.linkedin_url && (
                    <span className="inline-flex items-center gap-1 text-slate-500">
                      <ExternalLink className="h-3 w-3" />
                      LinkedIn
                    </span>
                  )}
                </div>
              </div>
            </div>
            {canDelete && (
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onAskDelete(c);
                }}
                aria-label={`Delete ${c.full_name}`}
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-md p-1.5 text-rose-400/40 opacity-0 transition-opacity hover:bg-rose-50 hover:text-rose-600 focus-visible:opacity-100 group-hover/contact:opacity-100"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

/** Surfaces the dirigeant scoring inputs (age, tenure) on a contact
 *  card for M&A projects. Tier thresholds mirror the default
 *  governance_succession tranches in the M&A scoring config — kept
 *  hardcoded for V1; revisit by reading rule.config.age /
 *  rule.config.tenure when operators start tweaking those tranches. */
function ContactScoringSignals({
  meta,
}: {
  meta: Record<string, unknown> | undefined;
}) {
  const age = readNumber(meta?.age);
  const tenure = readNumber(meta?.tenure_years);
  if (age == null && tenure == null) return null;

  return (
    <div className="flex flex-wrap items-center gap-3 pt-1">
      {age != null && (
        <div className="flex items-center gap-1">
          <User className="h-3 w-3 text-slate-400" />
          <span
            className={cn(
              "text-xs font-medium",
              age >= 65
                ? "text-emerald-600"
                : age >= 55
                  ? "text-amber-600"
                  : "text-slate-500",
            )}
          >
            {age} ans
          </span>
          {age >= 65 && (
            <span className="rounded bg-emerald-50 px-1 text-[9px] text-emerald-500">
              scoring ↑
            </span>
          )}
        </div>
      )}
      {tenure != null && (
        <div className="flex items-center gap-1">
          <Clock className="h-3 w-3 text-slate-400" />
          <span
            className={cn(
              "text-xs font-medium",
              tenure >= 20
                ? "text-emerald-600"
                : tenure >= 10
                  ? "text-amber-600"
                  : "text-slate-500",
            )}
          >
            {tenure} ans tenure
          </span>
          {tenure >= 20 && (
            <span className="rounded bg-emerald-50 px-1 text-[9px] text-emerald-500">
              scoring ↑
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function readNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() !== "") {
    const n = Number(value);
    if (Number.isFinite(n)) return n;
  }
  return null;
}

interface ScoreCircleButtonProps {
  score: number | null;
  hasOverride: boolean;
  interactive: boolean;
}

const ScoreCircleButton = forwardRef<HTMLButtonElement, ScoreCircleButtonProps>(
  ({ score, hasOverride, interactive, ...rest }, ref) => {
    const value = score ?? 0;
    const radius = 28;
    const circumference = 2 * Math.PI * radius;
    const offset = circumference * (1 - value / 100);

    return (
      <button
        ref={ref}
        type="button"
        disabled={!interactive}
        title={interactive ? "Click to override score" : undefined}
        aria-label={
          interactive
            ? `Score ${score ?? "—"}, click to override`
            : `Score ${score ?? "—"}`
        }
        className={cn(
          "group relative flex h-16 w-16 shrink-0 items-center justify-center rounded-full",
          interactive &&
            "cursor-pointer hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-indigo-300/50",
          !interactive && "cursor-default",
        )}
        {...rest}
      >
        <svg
          viewBox="0 0 64 64"
          className="absolute inset-0 -rotate-90"
          aria-hidden
        >
          <circle
            cx="32"
            cy="32"
            r={radius}
            fill="none"
            stroke="#e2e8f0"
            strokeWidth="4"
          />
          <circle
            cx="32"
            cy="32"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth="4"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className={scoreColor(score)}
          />
        </svg>
        <span className={cn("text-base font-semibold", scoreColor(score))}>
          {score ?? "—"}
        </span>
        {interactive && (
          <span className="absolute right-0.5 top-0.5 rounded-full bg-white p-0.5 opacity-0 shadow-sm ring-1 ring-slate-200 transition-opacity group-hover:opacity-100">
            <Pencil className="h-2.5 w-2.5 text-slate-500" />
          </span>
        )}
        {hasOverride && (
          <span className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 rounded-full border border-amber-200 bg-amber-50 px-1.5 py-0 text-[9px] font-semibold uppercase tracking-wider text-amber-700">
            Manual
          </span>
        )}
      </button>
    );
  },
);
ScoreCircleButton.displayName = "ScoreCircleButton";

function Card({
  id,
  title,
  headerActions,
  children,
  className,
}: {
  /** Anchor id — set on sections you want the toolbar / floating nav
   *  to scroll to. The `scroll-mt-24` below compensates for the
   *  page's sticky header so the section title isn't hidden under it. */
  id?: string;
  title?: string;
  /** Trailing slot in the header row — used for the per-section ⚙
   *  customiser trigger. Falls in line with the title via
   *  flex-justify-between, and the trigger fades in on card hover
   *  (group/card namespace) so the page stays calm at rest. */
  headerActions?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  const hasHeader = !!title || !!headerActions;
  return (
    <section
      id={id}
      className={cn(
        "group/card scroll-mt-24 rounded-plane bg-white p-5 shadow-plane",
        className,
      )}
    >
      {hasHeader && (
        <div className="mb-4 flex items-center justify-between">
          {title ? (
            <h2 className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
              {title}
            </h2>
          ) : (
            <span />
          )}
          {headerActions}
        </div>
      )}
      {children}
    </section>
  );
}

function asString(v: unknown): string | null {
  return typeof v === "string" ? v : null;
}

/** Stable JSON.stringify — recursively sorts object keys so
 *  two objects with the same contents but different insertion
 *  order serialise to the same string. Used by the dirty-state
 *  comparator so a re-keyed config (e.g. via spread + reorder)
 *  doesn't surface as a false positive. Arrays are preserved
 *  in their original order (semantically meaningful for the
 *  layout's section_order). */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") {
    return JSON.stringify(value);
  }
  if (Array.isArray(value)) {
    return "[" + value.map(stableStringify).join(",") + "]";
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return (
    "{" +
    keys
      .map((k) => JSON.stringify(k) + ":" + stableStringify(obj[k]))
      .join(",") +
    "}"
  );
}

/** Coerce an unknown value to a finite number; returns 0 on
 *  null / undefined / non-numeric strings. Used by the M&A
 *  score-circle fallback to sum metadata-stored scores. */
function toFiniteNumber(v: unknown): number {
  if (typeof v === "number") return Number.isFinite(v) ? v : 0;
  if (typeof v === "string") {
    const n = Number(v);
    return Number.isFinite(n) ? n : 0;
  }
  return 0;
}

function asNumber(v: unknown): number | null {
  return typeof v === "number" ? v : null;
}

function asBool(v: unknown): boolean | null {
  return typeof v === "boolean" ? v : null;
}

function labelForStatus(statuses: PipelineStatus[], key: string): string {
  return statuses.find((s) => s.key === key)?.label ?? key;
}

function extractErrorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
  }
  return "Update failed.";
}
